// SceneryHash — the JS mirror of the material system's GLSL PCG hash (Hash.glsl.ts), shared by
// `deriveVariants` and the placement layer. The hash MUST mirror the GLSL EXACTLY, so a future
// fragment-shader placement path (or a CPU/GPU consistency check) is bit-identical.
//
// ⚠ The negative-coordinate rule (plan §8): the GLSL casts the lattice coordinate via
// `uvec2(ivec2(...))`, which is UNDEFINED for negative values in GLSL ES 300 (drivers clamp to 0,
// collapsing the hash to a function of y alone — the dirt "pebble row-lock"). The JS mirror must
// apply the same wrap to stay consistent with the GPU: convert any signed coordinate to an
// unsigned 32-bit value by `>>> 0` (two's complement) BEFORE hashing. NEVER hash a negative value
// directly as a float→uint. This is invisible at positive coordinates, so it is tested explicitly.
import {asNumber, SceneryCollision} from "./@SceneryTypes.js";

/** The 32-bit PCG hash of a uint — byte-for-byte the GLSL `pcgHash` body. Returns an UNSIGNED
 *  32-bit value (0..4294967295). ⚠ The final `^` in JS coerces to a SIGNED int32; it must be
 *  masked back to unsigned (`>>> 0`) or the result can be negative — which would break `hash01`
 *  (a negative "probability") and any `% i` index arithmetic (the perm shuffle). */
export function pcgHash(x: number): number {
	// `>>> 0` keeps intermediate multiplications in uint32 space (matching the GLSL uint ops).
	let state = (Math.imul(x, 747796405) + 2891336453) >>> 0;
	let word = (((state >>> (((state >>> 28) + 4) & 31)) ^ state) * 277803737) >>> 0;
	return ((word >>> 22) ^ word) >>> 0;
}

/** Hash of a 2D cell → float in [0, 1). Mirrors GLSL `hash01(uvec2(v))`.
 *  @param cellX / cellY signed world lattice coords — wrapped to uint32 via `>>> 0` (the §8 rule). */
export function hash01(cellX: number, cellY: number): number {
	const vx = cellX >>> 0;
	const vy = cellY >>> 0;
	return pcgHash(vx ^ Math.imul(vy, 0x9E3779B1) >>> 0) / 4294967296;
}

/** Three decorrelated hashes of the same cell (mirrors GLSL `hash01_3`). */
export function hash01_3(cellX: number, cellY: number): [number, number, number] {
	const A = 0x9E3779B1;
	return [
		hash01(cellX, cellY),
		hash01(cellX + A, cellY + A),
		hash01(cellX + A * 2, cellY + A * 3),
	];
}

/** Stable integer hash of a string (FNV-1a 32-bit) — used to decorrelate per-param permutations
 *  from `(seed, paramKey)` and other named streams. Deterministic. */
export function hashString32(s: string): number {
	let h = 0x811c9dc5;
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i);
		h = Math.imul(h, 0x01000193) >>> 0;
	}
	return h;
}

/** A per-kind placement-hash base offset, derived from the kind's seed. EVERY placement stream
 *  (presence / variant / rank / rotation / scale / tint) adds this to the cell coords PLUS its
 *  own fixed sub-offset before hashing, so:
 *    (a) streams WITHIN a kind are decorrelated from each other (each has a distinct fixed
 *        offset — the material `scatter` +311u precedent — so the presence gate, the variant
 *        pick and the rank-thinning hash are independent, per §3.7);
 *    (b) kinds are decorrelated from EACH OTHER even when they share a seed (different seeds →
 *        different base → different placement/jitter for the same cells).
 * ⚠ NEVER write `seed * offset` as the decorrelator: with `seed == 0` (the default) every stream
 * collapses to `hash01(cx, cy)` and they become PERFECTLY correlated — a cell that passes the
 * presence gate always gets the same (low) variant + rank hashes, so high variants never place
 * and rank-thinning thins non-uniformly. The fixed-offset scheme above is immune to seed==0. */
export function scenerySeedOffset(seed: number): number {
	return hashString32(`scenerySeed:${seed}`) >>> 0;
}

/** Hash of a float input into [0,1) — a stable per-instance/param scalar stream. The value is
 *  hashed via its string form so the stream is stable across engines (Math.fround not required). */
export function hash01Of(name: string, a: number): number {
	return hash01(hashString32(`${name}:${a}`) >>> 0, 0);
}

/** Helper to resolve an optional/possibly-absent numeric field to a validated number, rejecting
 *  NaN/±Infinity. ⚠ THE plan's highest-risk footgun: `n > undefined` is `false`, so a bare
 *  comparison against a possibly-absent budget field SILENTLY VANISHES. Every budget field must
 *  resolve through a default FIRST (use `new ConfigClass(persisted)`, never `persisted ?? new C()`).
 *  This is the single validated-resolution seam for scenery's numeric fields. */
export function resolveNumber(v: number|n|undefined, fallback: number): number {
	if (typeof v != "number" || !Number.isFinite(v)) return fallback;
	return v;
}

/** Set17 — the single validated-resolution seam for a kind's COLLISION shape. THE shared helper so
 *  the resolver (`ResolvedSceneryKind.collision`) and the placer (`PlacementKindLike.collision`)
 *  can never disagree about how absent/null authored fields fall back.
 *  - `trunk`: authored ?? 0.25×spacing (a conservative hard ground-exclusion floor).
 *  - `crown`: authored ?? 0.5×spacing — EXCEPT an authored `null` crown, which is the ground-cover
 *    opt-out (a flower's crown is null → it never crown-collides with a pine's crown, but still
 *    trunk-collides with the pine's trunk) and is PRESERVED as `null`. `SceneryCollision.crown`
 *    defaults to a number (3), so a `null` value can only have been authored as `null`. */
export function resolveCollision(spacing: number, collision: SceneryCollision|n|undefined): SceneryCollision {
	const trunk = resolveNumber(collision?.trunk, 0.25 * spacing);
	const crown = collision != null && collision.crown === null
		? null
		: resolveNumber(collision?.crown, 0.5 * spacing);
	return new SceneryCollision({trunk, crown});
}

// Re-export asNumber so callers can resolve a scalar ParamValue with the same seam.
export {asNumber};
